import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, MapPin, ArrowLeft, Eye, Users } from 'lucide-react';

export default function ComingCompetitionsPage() {
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    axios.get(`${API}/public/coming-competitions`)
      .then(res => {
        setCompetitions(res.data);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, []);
  
  const formatDateRange = (startDate, endDate) => {
    if (!startDate) return 'TBA';
    try {
      const start = new Date(startDate);
      if (endDate) {
        const end = new Date(endDate);
        const sameDay = start.getDate() === end.getDate() && start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
        if (sameDay) {
          return start.toLocaleDateString('en-NZ', { day: 'numeric', month: 'long', year: 'numeric' });
        }
        if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
          return `${start.getDate()}-${end.getDate()} ${start.toLocaleDateString('en-NZ', { month: 'long', year: 'numeric' })}`;
        }
        return `${start.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' })}`;
      }
      return start.toLocaleDateString('en-NZ', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch {
      return startDate;
    }
  };
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F6' }}>
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-[#2D4A3E] hover:opacity-80">
            CompCreator
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" data-testid="home-button">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Home
              </Button>
            </Link>
            <Link to="/login">
              <Button className="btn-primary" data-testid="login-button">Login</Button>
            </Link>
          </div>
        </div>
      </nav>
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#2D4A3E] to-[#4A6B4A] text-white py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Coming Competitions
          </h1>
          <p className="text-lg text-green-100 max-w-2xl mx-auto">
            Discover upcoming artistic rollerskating events across New Zealand
          </p>
        </div>
      </div>
      
      {/* Competition Cards */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-700 mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading competitions...</p>
          </div>
        ) : competitions.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {competitions.map(comp => (
              <Card 
                key={comp.competition_id} 
                className="p-6 hover:shadow-lg transition-shadow duration-200 border-l-4 border-l-green-600"
                data-testid={`competition-card-${comp.competition_id}`}
              >
                {/* Competition Name */}
                <h3 className="text-xl font-semibold text-[#2D4A3E] mb-3 line-clamp-2">
                  {comp.name}
                </h3>
                
                {/* Sub-competition if exists */}
                {comp.sub_competition_name && (
                  <p className="text-sm text-amber-700 font-medium mb-3">
                    Combined with: {comp.sub_competition_name}
                  </p>
                )}
                
                {/* Disciplines */}
                <div className="flex gap-2 mb-4">
                  {comp.artistic_events_included && (
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">
                      Artistic
                    </span>
                  )}
                  {comp.figure_events_included && (
                    <span className="px-2 py-1 bg-amber-100 text-amber-800 text-xs font-medium rounded">
                      Figures
                    </span>
                  )}
                </div>
                
                {/* Date */}
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                  <Calendar className="h-4 w-4 text-green-600" />
                  <span>{formatDateRange(comp.start_date, comp.end_date)}</span>
                </div>
                
                {/* Location */}
                {(comp.venue_name || comp.city) && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                    <MapPin className="h-4 w-4 text-green-600" />
                    <span>
                      {comp.venue_name && comp.city 
                        ? `${comp.venue_name}, ${comp.city}` 
                        : comp.venue_name || comp.city}
                    </span>
                  </div>
                )}
                
                {/* Host Organisation */}
                {comp.host_organisation_name && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                    <Users className="h-4 w-4 text-green-600" />
                    <span>Hosted by {comp.host_organisation_name}</span>
                  </div>
                )}
                
                {/* View Button */}
                <Link to={`/competition/${comp.page_id}`}>
                  <Button className="w-full btn-primary mt-2" data-testid={`view-competition-${comp.competition_id}`}>
                    <Eye className="mr-2 h-4 w-4" />
                    View Details
                  </Button>
                </Link>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Calendar className="h-16 w-16 text-stone-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-stone-600 mb-2">No Upcoming Competitions</h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              There are no competitions currently scheduled. Check back soon for upcoming events!
            </p>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <footer className="bg-[#2D4A3E] text-white py-8 mt-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-green-200 text-sm">
            © {new Date().getFullYear()} CompCreator. All rights reserved.
          </p>
          <p className="text-green-300 text-xs mt-2">
            Brought to you by CompCreator
          </p>
        </div>
      </footer>
    </div>
  );
}
